import {
  Badge,
  Box,
  Checkbox,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { BidV2Status, type EvalBidDto } from "../../../../autogen/bff-api";
import { PriceCell } from "./PriceCell";

interface Props {
  bids: EvalBidDto[];
  readOnly: boolean;
  awarded: boolean;
  selectedBidIds: string[];
  onToggleSelection: (bidId: string) => void;
  onPriceOverride: (bidId: string, value: number | null, comment: string | undefined) => Promise<void>;
}

/**
 * ADR-033 D3: the price evaluation model — events without award criteria rank by
 * effective price alone. One row per bid in rank order; the price is override-aware
 * and, in a round context, shown against the supplier's original bid. During
 * evaluation the buyer marks the selected bid(s) — the award's default winners.
 */
export const PriceEvaluationView = ({
  bids,
  readOnly,
  awarded,
  selectedBidIds,
  onToggleSelection,
  onPriceOverride,
}: Props) => {
  const { t } = useTranslation();
  const showChange = bids.some((bid) => bid.originalPrice?.value != null);

  return (
    <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
      <TableContainer>
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th w="12">#</Th>
              {!awarded && <Th w="12">{t("Selected")}</Th>}
              <Th>{t("Supplier")}</Th>
              <Th textAlign="right">{t("Price")}</Th>
              {showChange && <Th textAlign="right">{t("Change since original bid")}</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {bids.map((bid) => {
              const isRejected = bid.status === BidV2Status.Rejected;
              const isSelected = awarded
                ? bid.status === BidV2Status.Awarded
                : selectedBidIds.includes(bid.bidId);
              return (
                <Tr key={bid.bidId} bg={isRejected ? "red.50" : isSelected ? "green.50" : undefined}>
                  <Td fontWeight="bold" fontFamily="mono" color={isRejected ? "red.700" : "smMuted"}>
                    {isRejected || bid.rank <= 0 ? "–" : bid.rank}
                  </Td>
                  {!awarded && (
                    <Td>
                      <Checkbox
                        aria-label={`${t("Selected")}: ${bid.supplierName}`}
                        colorScheme="green"
                        isChecked={isSelected}
                        isDisabled={readOnly || isRejected}
                        onChange={() => onToggleSelection(bid.bidId)}
                      />
                    </Td>
                  )}
                  <Td fontWeight="medium" color="smText">
                    {bid.supplierName || t("Unknown")}{" "}
                    {isRejected && <Badge colorScheme="red">{t("Avvist")}</Badge>}
                    {isSelected && <Badge colorScheme="green">{t("Selected")}</Badge>}
                  </Td>
                  <Td textAlign="right">
                    <PriceCell
                      bid={bid}
                      readOnly={readOnly || isRejected}
                      onSave={(value, comment) => onPriceOverride(bid.bidId, value, comment)}
                    />
                  </Td>
                  {showChange && (
                    <Td textAlign="right">
                      <ChangeSinceOriginal bid={bid} />
                    </Td>
                  )}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const ChangeSinceOriginal = ({ bid }: { bid: EvalBidDto }) => {
  const { t } = useTranslation();
  const original = bid.originalPrice?.value;
  const current = bid.effectivePrice?.value;
  if (original == null || current == null || original === 0) {
    return (
      <Text fontSize="sm" color="smMuted">
        {t("n/a")}
      </Text>
    );
  }
  const change = current - original;
  const percent = (change / original) * 100;
  const color = change > 0 ? "smError" : change < 0 ? "smSuccess" : "smMuted";
  return (
    <Text fontSize="sm" color={color} fontFamily="mono">
      {`${change > 0 ? "+" : ""}${percent.toFixed(1)}% (${change > 0 ? "+" : ""}${change.toLocaleString("nb-NO")})`}
    </Text>
  );
};
